import React, { useState, useEffect } from "react";

// ESRI
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import IdentityManager from "@arcgis/core/identity/IdentityManager";

// lIbraries
import { Button, Icon, Dropdown, Popup } from "semantic-ui-react";
import axios from "axios";

// Custom
import DrawHandler from "./DrawHandler";

const PrintServiceWidget = ({
  url,
  view,
  activeTool,
  deactivateTool,
  activateTool,
  setTooltipMessage,
  setTooltipVisible,
  localConfig,
  setLocalConfig,
  token,
}) => {
  const [status, setStatus] = useState(false);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [layer, setLayer] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [drawHandler, setDrawHandler] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // console.log("PrintServiceWidget", { url, activeTool, status, job });

  useEffect(() => {
    const print_layer = new GraphicsLayer({
      id: "PrintServiceLayer",
      // print: true,
    });
    view.map.layers.add(print_layer);
    setLayer(print_layer);

    // Widgets for the drawing tool
    const draw_handler = new DrawHandler({
      view: view,
      layer: print_layer,
    });

    draw_handler.on("completed", (geometry) => {
      // Submit the job
      submitJob(geometry.extent);
      setTooltipMessage(null);
      setTooltipVisible(false);

      // Clear the tooltip
      // this.cancelTooltip();

      setTimeout(() => {
        // Clear the graphics
        print_layer.graphics.removeAll();

        // If the restart tool is set then activate it
        // if (this.restart_tool) {
        activateTool({ tool: "Identify" });
      }, 500);
    });

    setDrawHandler(draw_handler);

    // Check if there are any active print reqeusts
    if (localConfig.print_request) {
      checkPrintStatus(localConfig.print_request);
    }
  }, []);

  const checkPrintStatus = async (job_id) => {
    console.error({ job_id, url });

    let fetchReport = () => axios.get(`${url}/jobs/${job_id}?f=json`);
    let validate = (result) => {
      // console.log("validate", { result });

      // Catch if the print fails
      if (result.data.jobStatus === "esriJobFailed") {
        setErrorMessage("Print Request failed. Click to clear.");
        setStatus("error");
        return false;
      }

      // Cancel if error
      if (result.data.error) {
        setErrorMessage("Print Request no longer exists. Click to clear.");
        setStatus("error");
        return false;
      }

      if (
        ["esriJobCancelled", "esriJobCancelling"].includes(
          result.data.jobStatus
        )
      ) {
        reset();
        return false;
      }

      // Cancel if the job succeeded
      if (result.data.jobStatus === "esriJobSucceeded") {
        // setStatus("finished");
        // setFileUrl(result.data.results.value.url);
        // setJob(null);
        return false;
      }

      if (result.data.jobStatus === "esriJobExecuting") {
        setStatus("active");
      }

      // continue looping
      return true;
    };
    let response = await poll(fetchReport, validate, 5000);

    if (response.data.jobStatus === "esriJobSucceeded") {
      axios
        .get(`${url}/jobs/${job_id}/results/output_file?f=json`)
        .then((result) => {
          // console.log("GET FILE", { result });
          setStatus("finished");
          setFileUrl(result.data.value.url);
          setJob(null);
        });
    }

    console.error("Response", { response });
  };

  const poll = async function (fn, fnCondition, ms) {
    let result = await fn();
    // console.log("RESULT", { result });
    while (fnCondition(result)) {
      await wait(ms);
      result = await fn();
    }
    return result;
  };

  const wait = function (ms = 1000) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  };

  const handlePortrait = () => {
    submitJob("dmcgisPortrait");
  };

  const handleLandscape = () => {
    submitJob("dmcgisLandscape");
  };

  const reset = () => {
    setStatus(false);
    setFileUrl(null);
    var new_config = { ...localConfig };
    new_config.print_request = null;

    setLocalConfig(new_config);
  };

  function mapLayerType(layer) {
    switch (layer.type) {
      case "tile":
        return "ArcGISTiledMapServiceLayer";
      case "map-image":
        return "ArcGISMapServiceLayer";
      case "feature":
        return "Feature Layer";
      default:
        return layer.type; // fallback
    }
  }

  function serializeSublayer(sublayer) {
    const json = {
      id: sublayer.id,
      name: sublayer.title,
      layerDefinition: {
        source: {
          type: "mapLayer",
          mapLayerId: sublayer.id,
        },
      },
    };

    if (sublayer.definitionExpression) {
      json.layerDefinition.definitionExpression = sublayer.definitionExpression;
    }

    if (sublayer.renderer) {
      json.layerDefinition.drawingInfo = {
        renderer: sublayer.renderer.toJSON(),
      };
    }

    if (sublayer.sublayers && sublayer.sublayers.length) {
      json.sublayers = sublayer.sublayers
        .filter((sublayer) => sublayer.visible)
        .map(serializeSublayer);
    }

    return json;
  }

  function serializeGraphicsLayer(layer) {
    if (!layer.visible || !layer.print) return null;

    const graphicsArr = Array.isArray(layer.graphics)
      ? layer.graphics
      : (layer.graphics && layer.graphics.items) || [];

    if (!graphicsArr.length) return null;

    // Separate graphics into categories
    const polygons = [],
      polylines = [],
      points = [],
      textSymbols = [];

    graphicsArr.forEach((g) => {
      if (!g.geometry) return;

      if (g.symbol?.type === "text") {
        textSymbols.push(g);
      } else if (g.geometry.type === "polygon" || g.geometry.rings) {
        polygons.push(g);
      } else if (g.geometry.type === "polyline" || g.geometry.paths) {
        polylines.push(g);
      } else if (g.geometry.type === "point" || g.geometry.x !== undefined) {
        points.push(g);
      }
    });

    const layerGroups = [
      { type: "polygon", features: polygons },
      { type: "polyline", features: polylines },
      { type: "point", features: points },
      { type: "text", features: textSymbols },
    ];

    const layers = [];

    layerGroups.forEach((group) => {
      if (!group.features.length) return;

      // console.log({ group });

      const features = group.features.map((g) => {
        let geomJSON;
        if (group.type === "polygon") {
          geomJSON = {
            type: "polygon",
            rings: g.geometry.rings,
            spatialReference: g.geometry.spatialReference,
          };
        } else if (group.type === "polyline") {
          geomJSON = {
            type: "polyline",
            paths: g.geometry.paths,
            spatialReference: g.geometry.spatialReference,
          };
        } else {
          geomJSON = {
            type: "point",
            x: g.geometry.x,
            y: g.geometry.y,
            spatialReference: g.geometry.spatialReference,
          };
        }

        const symbol = g.symbol
          ? g.symbol.toJSON
            ? g.symbol.toJSON()
            : g.symbol
          : getDefaultSymbolForGraphic(g);

        return { geometry: geomJSON, symbol, aggregateGeometries: null };
      });

      let esriGeomType;
      switch (group.type) {
        case "polygon":
          esriGeomType = "esriGeometryPolygon";
          break;
        case "polyline":
          esriGeomType = "esriGeometryPolyline";
          break;
        default:
          esriGeomType = "esriGeometryPoint";
          break; // point, text
      }

      layers.push({
        layerDefinition: {
          name: `${layer.id}_${group.type}`,
          geometryType: esriGeomType,
        },
        featureSet: { geometryType: esriGeomType, features },
      });
    });

    if (!layers.length) return null;

    return {
      id: layer.id,
      title: layer.title || null,
      opacity: layer.opacity != null ? layer.opacity : 1,
      minScale: layer.minScale || 0,
      maxScale: layer.maxScale || 0,
      layerType: "Feature Layer",
      featureCollection: { layers },
    };
  }

  // Fallback symbol generator for non-text graphics
  function getDefaultSymbolForGraphic(graphic) {
    const geomType = graphic.geometry?.type;
    switch (geomType) {
      case "point":
        return {
          type: "esriSMS",
          style: "esriSMSCircle",
          color: [255, 0, 0, 128],
          size: 10,
          outline: { type: "esriSLS", color: [0, 0, 0, 255], width: 1 },
        };
      case "polyline":
        return { type: "esriSLS", color: [255, 0, 0, 255], width: 2 };
      case "polygon":
        return {
          type: "esriSFS",
          style: "esriSFSSolid",
          color: [255, 0, 0, 128],
          outline: { type: "esriSLS", color: [0, 0, 0, 255], width: 1 },
        };
      default:
        return {};
    }
  }

  function serializeLayer(layer) {
    if (!layer.visible) return null; // skip invisible layers

    if (layer.type === "graphics" && layer.print) {
      return serializeGraphicsLayer(layer);
    }

    let url = layer.url;

    if (layer.url) {
      const cred = IdentityManager.findCredential(layer.url);
      if (cred) {
        // console.log("Secured:", layer.url, "Token:", cred.token);
        url = `${layer.url}?f=json&token=${token}`;
        // } else {
        //   console.log("Likely public:", layer.url);
      }
    }

    const json = {
      id: layer.id,
      title: layer.title,
      url: url,
      opacity: layer.opacity,
      layerType: mapLayerType(layer),
      minScale: layer.minScale || 0,
      maxScale: layer.maxScale || 0,
    };

    if (layer.type === "map-image") {
      json.sublayers = layer.sublayers
        .filter((sublayer) => sublayer.visible)
        .map(serializeSublayer);
    }

    return json;
  }

  const cancelJob = () => {
    // console.log("cancel job");
    if (job) {
      job.cancelJob(job.jobId);
    }
    reset();
  };

  const submitJob = async (template) => {
    // console.error("submitJob");

    // Set the web_map_as_json default object
    var web_map_as_json = {
      operationalLayers: view.map.layers.map(serializeLayer),
      mapOptions: {
        showAttribution: false,
        extent: view.extent,
        scale: view.scale,
      },
      exportOptions: { dpi: 150 },
    };

    var parameters = {
      Web_Map_as_JSON: web_map_as_json,
      Format: "PDF",
      Layout_Template: template,
      f: "json",
    };

    // console.log("parameters", JSON.stringify(parameters));
    // console.log("---------------------");

    setStatus("active");
    // console.log({ url, parameters });

    const job = await geoprocessor.submitJob(url, parameters);
    setJob(job);

    // console.error({ job });

    var new_config = { ...localConfig };
    new_config.print_request = job.jobId;

    setLocalConfig(new_config);

    checkPrintStatus(job.jobId);
  };

  const checkDropdownVisibility = () => {
    const $dropdown_menu = document.getElementById("print_dropdown_menu");
    if ($dropdown_menu.classList.contains("visible")) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  if (status === "error") {
    return (
      <Popup
        trigger={
          <Button
            icon
            negative
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => reset()}
          >
            <Icon name={"exclamation"} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content={
          errorMessage
            ? errorMessage
            : "An Error has occurred. Print Task Failed."
        }
        position="right center"
      />
    );
  }

  if (status === "active") {
    return (
      <Popup
        trigger={
          <Button
            icon
            secondary
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => cancelJob()}
          >
            <Icon name={"notched circle"} loading style={{ height: "1em" }} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content="Processing Print Request. Click to Cancel."
        position="right center"
      />
    );
  }

  if (status === "finished") {
    return (
      <Popup
        trigger={
          <Button
            as="a"
            target="_blank"
            href={fileUrl}
            icon
            positive
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => {
              reset();
            }}
          >
            <Icon name={"checkmark"} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content="Print Request Completed"
        position="right center"
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Popup
        content="Print"
        position="right center"
        open={open}
        className={`transition fade in`}
        inverted
        size="tiny"
        trigger={
          <Dropdown
            icon="print"
            pointing="left"
            className="ui icon button no-margin no-focus"
            onMouseEnter={() => checkDropdownVisibility()}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen(false)}
          >
            <Dropdown.Menu id="print_dropdown_menu">
              <Dropdown.Header content="Print Map" />
              <Dropdown.Divider />
              <Dropdown.Item onClick={handlePortrait}>
                <Icon className="expand arrows alternate" />
                Portrait
              </Dropdown.Item>
              <Dropdown.Item onClick={handleLandscape}>
                <Icon className="expand arrows alternate" />
                Landscape
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
    </div>
  );
};

export default PrintServiceWidget;
